import React, {useContext, useState} from "react";
import {Box, Paragraph, Text, ThemeContext, Tip} from "grommet";
import useSWR, {mutate} from "swr";
import {useRouter} from "next/router";
import {handleErrors} from "../../../utils/errors";
import {formatLastSynced} from "../../../utils/dates";
import LinkHubSpot from "../../LinkHubSpot";
import {DialogContext} from "../../DialogProvider";
import {NotificationsContext} from "../../NotificationsProvider";
import {syncHubSpot, unlinkHubSpot} from "../../../utils/connections";
import ConnectionCard from "./ConnectionCard";
import {faSync as IconSync, faChevronDown as IconChevronDown, faCircleInfo as IconInfo} from "@fortawesome/pro-light-svg-icons";
import FontAwesomeIcon from "../../FontAwesomeIcon";
import Button from "../../Button";
import { MainButtonWithExtension, ExtensionButton } from "../../../utils/styled";
import NextAnchor from "../../NextAnchor";

const HubSpotConnectionComponent = ({
    user,
    canAddCommerceIntegration=true,
    maxCommerceIntegrations,
    onIntegrationChange,
    showConnectionDetails=true,
}) => {
    const [connectionData, setConnectionData] = useState();
    const notificationCtx = useContext(NotificationsContext);
    const dialogCtx = useContext(DialogContext);
    const router = useRouter();
    const theme = useContext(ThemeContext);

    const fetcher = (url) =>
        fetch(url).then(handleErrors).catch(notificationCtx.showNotification);

    useSWR("/api/settings/hubspot", fetcher, {
        revalidateOnFocus: false,
        onSuccess: (data) => {
            setConnectionData(data || null);
        }
    });

    const isLinked = connectionData && connectionData.connected;

    const refetchDetails = () => {
        mutate("/api/settings/hubspot");
    }

    const renderIntegrationLimitMessage = () => (
        <Box
            direction="row"
            align="center"
            gap="small"
            pad="small"
            round="xsmall"
            background="background-contrast"
        >
            <Tip content={`Your plan allows ${maxCommerceIntegrations} commerce ${maxCommerceIntegrations === 1 ? 'integration' : 'integrations'}.`}>
                <Box flex={false}>
                    <FontAwesomeIcon SelectedIcon={IconInfo} size="xxsmall" color="text-weak" />
                </Box>
            </Tip>
            <Text size="xsmall" color="text-weak">
                <NextAnchor hoverIndicator href="https://streamlinedpayments.com/pricing">Upgrade</NextAnchor> or disconnect an integration to connect.
            </Text>
        </Box>
    )

    const renderHubSpotOauth = () => {
        if (!canAddCommerceIntegration) {
            return renderIntegrationLimitMessage();
        }

        return (
            <Box>
                <LinkHubSpot
                    userId={user?.id}
                    refreshPage={false}
                    onLinked={() => {
                        onIntegrationChange?.();
                    }}
                    ctaColor="status-ok"
                    buttonWrapperProps={{ pad: {vertical: 'xsmall'} }}
                />
            </Box>
        );
    }

    const buildMenuList = () => {
        return [[
            {
                label: "Settings",
                onClick: () => { router.push(`/settings/connections/hubspot/settings`)},
                event: {section: 'hubspot_connection_card', element: 'settings'}
            },
            {
                label: (<Text color='status-critical'>Unlink HubSpot</Text>),
                onClick: () => {
                    dialogCtx.showDialog({
                        title: 'Unlink HubSpot',
                        message: <Paragraph fill margin={{vertical: 'none'}}>Are you sure you would like to unlink <Text weight="bold">{connectionData?.portal_name || 'HubSpot'}</Text> from Streamlined?</Paragraph>,
                        callback: () => unlinkHubSpot(
                            notificationCtx.showNotification,
                            () => {
                                refetchDetails();
                                onIntegrationChange?.();
                            }
                        ),
                        ctaText: "Unlink",
                        ctaType: "error"
                    });
                },
                event: {section: 'hubspot_connection_card', element: 'unlink'}
            },
        ]];
    }

    const getCta = () => {
        if (isLinked) {
            return (
                <HubSpotSyncButton
                    showConnectionDetails={showConnectionDetails}
                    showNotification={notificationCtx.showNotification}
                    onSync={refetchDetails}
                    connectionData={connectionData}
                />
            )
        }
        return null;
    }

    const getDetails = () => {
        if (!isLinked) {
            return [
                'Connect your HubSpot account to start syncing quotes automatically.',
            ]
        }
        const lastSynced = connectionData?.last_sync_at;
        return [
            connectionData?.portal_name || 'Connected',
            formatLastSynced(lastSynced)
        ].filter(Boolean);
    }

    return (
        <ConnectionCard
            logoSrc={`/brand_logos/hubspot${theme.dark ? '_white' : ''}.svg`}
            connectionName="HubSpot"
            isLoaded={connectionData !== undefined}
            nested
            menuItems={isLinked ? buildMenuList() : null}
            details={getDetails()}
            cta={isLinked ? getCta() : renderHubSpotOauth()} />
    )
};

const HubSpotSyncButton = ({ connectionData, buttonProps, showConnectionDetails, showNotification, onSync}) => {
    const [isSyncing, setIsSyncing] = useState(connectionData?.sync_in_progress === true);
    const theme = useContext(ThemeContext);

    const reSyncHubSpot = (lookbackDays = null) => {
        setIsSyncing(true);
        syncHubSpot(showNotification, onSync, ()=> {
            setIsSyncing(false)
        }, lookbackDays);
    };

    return (
        <Box direction="row" fill="horizontal">
            <MainButtonWithExtension
                event={{section: 'hubspot_connection_card', element: 'sync_hubspot'}}
                fill="horizontal"
                icon={
                    <Box animation={ isSyncing ? { type: "rotateRight", duration: 1350 } : undefined }>
                        <FontAwesomeIcon SelectedIcon={IconSync} size="xsmall"/>
                    </Box>
                }
                disabled={isSyncing}
                label={<Text color="text">{isSyncing ? "Sync in progress" : "Sync HubSpot"}</Text>}
                onClick={() => reSyncHubSpot()}
                hideRightBorder
                {...(showConnectionDetails ? {primary: true, color: "status-ok", ctaColor: isSyncing ? 'neutral-5' : 'status-ok'} : {secondary: true})}
                {...buttonProps}
            />
            <ExtensionButton
                event={{section: 'hubspot_connection_card', element: 'sync_hubspot_lookback'}}
                disabled={isSyncing}
                dropProps={{ align: { top: "bottom", right: "right" } }}
                margin={{left: 'xxsmall'}}
                icon={<FontAwesomeIcon SelectedIcon={IconChevronDown} size="xxsmall" color="text" />}
                dropContent={
                    <Box pad="xsmall" border>
                        {[
                            { label: "From 1 week ago", days: 7 },
                            { label: "From 2 weeks ago", days: 14 },
                            { label: "From 1 month ago", days: 30 },
                            { label: "From 2 months ago", days: 60 },
                            { label: "From 3 months ago", days: 90 },
                        ].map(option => (
                            <Button
                                key={option.days}
                                event={{section: 'hubspot_connection_card', element: 'sync_lookback', description: option.label}}
                                plain
                                hoverIndicator
                                onClick={() => reSyncHubSpot(option.days)}
                                label={<Text>{option.label}</Text>}
                                style={{ padding: '8px 12px', width: '100%', textAlign: 'left' }}
                            />
                        ))}
                    </Box>
                }
                {...(showConnectionDetails ? {primary: true, color: "status-ok", ctaColor: isSyncing ? 'neutral-5' : 'status-ok'} : {secondary: true})}
            />
        </Box>
    );
};

export default HubSpotConnectionComponent;
